import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { getAppointments, createAppt, updateAppt, deleteAppt, getUsers } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../components/ConfirmDialog';
import { formatDate, formatTime } from '../../utils/formatDateTime';
import PhoneInput from '../../components/PhoneInput';
import { formatPhoneBR, isValidPhoneBR } from '../../utils/phone';
import PageTour from '../../components/tenant/PageTour';

const TOUR_STEPS = [
  {
    selector: '[data-tour="appt-new-btn"]',
    title: 'Cadastro manual',
    content: 'Cliente ligou ou apareceu sem marcar? Cadastre o agendamento aqui direto.',
    placement: 'bottom',
  },
  {
    selector: '[data-tour="appt-filters"]',
    title: 'Filtros',
    content: 'Filtre por data, status ou nome do cliente pra encontrar rapidinho.',
    placement: 'bottom',
  },
  {
    selector: '[data-tour="appt-list"]',
    title: 'Lista completa',
    content: 'Agendamentos do link público chegam aqui automaticamente. Marque como "Concluído" depois do atendimento — isso alimenta a receita no Dashboard.',
    placement: 'top',
  },
];

const S_LABEL = { scheduled:'Agendado', completed:'Concluído', cancelled:'Cancelado', no_show:'Não compareceu' };
const S_BADGE = { scheduled:'badge-info', completed:'badge-success', cancelled:'badge-danger', no_show:'badge-gray' };
const EMPTY   = { owner_user_id:'', client_name:'', client_contact:'', client_email:'', datetime:'', duration_minutes:60, notes:'', status:'scheduled' };

export default function TenantAppointments() {
  const { user, hasPermission, hasFeature } = useAuth();
  const confirm = useConfirm();
  const [items,     setItems]     = useState([]);
  const [users,     setUsers]     = useState([]);
  const [units,     setUnits]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState(EMPTY);
  const [saving,    setSaving]    = useState(false);
  const [acting,    setActing]    = useState({});
  const [filters,   setFilters]   = useState({ status:'', date_from:'', date_to:'', search:'', unit_id:'' });

  const canCreate     = hasPermission('CREATE_APPOINTMENT');
  const canEdit       = hasPermission('EDIT_APPOINTMENT');
  const canDelete     = hasPermission('DELETE_APPOINTMENT');
  const canViewAll    = hasPermission('VIEW_ALL_APPOINTMENTS');
  const canManageUsers = hasPermission('MANAGE_USERS');

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filters.status)    params.status    = filters.status;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to)   params.date_to   = filters.date_to;
    if (filters.search)    params.search    = filters.search;
    if (filters.unit_id)   params.unit_id   = filters.unit_id;
    // Only fetch users list if user has MANAGE_USERS permission (required by the API)
    const fetchUnits = hasFeature('multi_unit')
      ? import('../../services/api').then(m => m.listUnits()).catch(() => ({ data: { units: [] } }))
      : Promise.resolve({ data: { units: [] } });
    Promise.all([
      getAppointments(params),
      canManageUsers ? getUsers() : Promise.resolve({ data:{ users:[] } }),
      fetchUnits,
    ]).then(([a, u, un]) => { setItems(a.data.appointments); setUsers(u.data.users); setUnits(un.data.units || []); })
      .catch(() => toast.error('Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [filters, canManageUsers]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, owner_user_id: user.id });
    setShowModal(true);
  };
  const openEdit = a => {
    setEditing(a);
    setForm({ owner_user_id:a.owner_user_id, client_name:a.client_name, client_contact:a.client_contact||'',
              client_email:a.client_email||'', datetime:a.datetime?.slice(0,16), duration_minutes:a.duration_minutes||60,
              notes:a.notes||'', status:a.status });
    setShowModal(true);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.client_name.trim() || !form.datetime) return toast.error('Nome e data/hora são obrigatórios');
    setSaving(true);
    try {
      if (editing) { await updateAppt(editing.id, form); toast.success('Agendamento atualizado!'); }
      else         { await createAppt(form);              toast.success('Agendamento criado!'); }
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const withActing = (key, fn) => async () => {
    setActing(p => ({...p,[key]:true}));
    try { await fn(); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Erro'); }
    finally { setActing(p => ({...p,[key]:false})); }
  };

  const setF = (k, v) => setFilters(f => ({...f,[k]:v}));
  const clearFilters = () => setFilters({ status:'', date_from:'', date_to:'', search:'' });
  const hasFilters = filters.status || filters.date_from || filters.date_to || filters.search || filters.unit_id;

  return (
    <div className="page-body">
      <div className="page-header-row">
        <div className="page-header">
          <h2>Agendamentos</h2>
          <p>{items.length} agendamento(s)</p>
          <div style={{ marginTop: 8 }}>
            <PageTour tourId="appointments" steps={TOUR_STEPS} />
          </div>
        </div>
        {canCreate && <button className="btn btn-primary" data-tour="appt-new-btn" onClick={openCreate}>+ Novo Agendamento</button>}
      </div>

      <div className="filter-bar" data-tour="appt-filters">
        <input className="search-input" placeholder="Buscar cliente..." value={filters.search} onChange={e => setF('search', e.target.value)} />
        <select value={filters.status} onChange={e => setF('status', e.target.value)} style={{ maxWidth:150 }}>
          <option value="">Todos status</option>
          <option value="scheduled">Agendados</option>
          <option value="completed">Concluídos</option>
          <option value="cancelled">Cancelados</option>
          <option value="no_show">Não compareceu</option>
        </select>
        <div style={{ display:'flex', flexDirection:'column', gap:2, flex:1, minWidth:140 }}>
          <label style={{ fontSize:11, fontWeight:600, color:'var(--gray-500)', paddingLeft:2 }}>📅 De</label>
          <div className="date-input-wrap"><input type="date" value={filters.date_from} onChange={e => setF('date_from', e.target.value)} style={{ maxWidth:'100%' }} /></div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:2, flex:1, minWidth:140 }}>
          <label style={{ fontSize:11, fontWeight:600, color:'var(--gray-500)', paddingLeft:2 }}>📅 Até</label>
          <div className="date-input-wrap"><input type="date" value={filters.date_to} onChange={e => setF('date_to', e.target.value)} style={{ maxWidth:'100%' }} /></div>
        </div>
        {units.length > 0 && (
          <select value={filters.unit_id} onChange={e => setF('unit_id', e.target.value)} style={{ maxWidth:160 }}>
            <option value="">Todas unidades</option>
            {units.map(u => <option key={u.id} value={u.id}>{u.is_headquarters ? '🏠 ' : '🏢 '}{u.name}</option>)}
          </select>
        )}
        {hasFilters && <button className="btn btn-ghost btn-sm" onClick={clearFilters}>✕ Limpar</button>}
      </div>

      {loading ? <div className="spinner" style={{ margin:'40px auto', display:'block' }} /> : (
        items.length === 0
          ? <div className="empty-state">
              <div className="empty-icon">📅</div>
              <h3>Nenhum agendamento encontrado</h3>
              <p>{hasFilters ? 'Tente limpar os filtros' : ''}</p>
              {canCreate && !hasFilters && <button className="btn btn-primary btn-sm" style={{marginTop:12}} onClick={openCreate}>Criar agendamento</button>}
            </div>
          : <div data-tour="appt-list" style={{ display:'flex', flexDirection:'column', gap:12 }}>
              {items.map(a => (
                <div key={a.id} className="mobile-card">
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                        <strong style={{ fontSize:15 }}>{a.client_name}</strong>
                        <span className={`badge ${S_BADGE[a.status]||'badge-gray'}`}>{S_LABEL[a.status]||a.status}</span>
                      </div>
                      {(a.client_contact||a.client_email) && (
                        <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:2 }}>
                          {a.client_contact
                            ? (isValidPhoneBR(a.client_contact) ? formatPhoneBR(a.client_contact) : a.client_contact)
                            : a.client_email}
                        </div>
                      )}
                      {a.notes && <div style={{ fontSize:12, color:'var(--gray-400)', marginTop:2, fontStyle:'italic' }}>{a.notes}</div>}
                    </div>
                    <div style={{ textAlign:'right', flexShrink:0 }}>
                      <div style={{ fontWeight:700, fontSize:14 }}>{formatDate(a.datetime)}</div>
                      <div style={{ fontSize:13, color:'var(--primary)', fontWeight:600 }}>{formatTime(a.datetime)}</div>
                      <div style={{ fontSize:11, color:'var(--gray-400)' }}>{a.duration_minutes}min</div>
                    </div>
                  </div>
                  {(canViewAll && a.owner_name) && (
                    <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:6 }}>Responsável: <strong>{a.owner_name}</strong></div>
                  )}
                  {(canEdit||canDelete) && (
                    <div style={{ display:'flex', gap:8, marginTop:12, flexWrap:'wrap' }}>
                      {canEdit && a.status==='scheduled' && (
                        <>
                          <button className={`btn btn-success btn-sm${acting[`complete-${a.id}`]?' btn-loading':''}`} disabled={acting[`complete-${a.id}`]}
                            onClick={withActing(`complete-${a.id}`, async () => { await updateAppt(a.id,{status:'completed'}); toast.success('Concluído!'); })}>
                            {acting[`complete-${a.id}`] ? '' : 'Concluir'}
                          </button>
                          <button className={`btn btn-warning btn-sm${acting[`cancel-${a.id}`]?' btn-loading':''}`} disabled={acting[`cancel-${a.id}`]}
                            onClick={withActing(`cancel-${a.id}`, async () => { await updateAppt(a.id,{status:'cancelled'}); toast.success('Cancelado'); })}>
                            {acting[`cancel-${a.id}`] ? '' : 'Cancelar'}
                          </button>
                        </>
                      )}
                      {canEdit && <button className="btn btn-secondary btn-sm" onClick={() => openEdit(a)}>Editar</button>}
                      {canDelete && <button className={`btn btn-danger btn-sm${acting[`del-${a.id}`]?' btn-loading':''}`} disabled={acting[`del-${a.id}`]}
                        onClick={withActing(`del-${a.id}`, async () => {
                          const ok = await confirm({
                            title: `Excluir agendamento de ${a.client_name}?`,
                            message: 'Esta ação remove o agendamento do sistema. O cliente NÃO recebe notificação automática — se quiser avisar, faça isso antes de excluir.',
                            confirmText: 'Excluir',
                            variant: 'danger',
                          });
                          if (!ok) throw new Error('cancelled');
                          await deleteAppt(a.id);
                          toast.success('Removido!');
                        })}>
                        {acting[`del-${a.id}`] ? '' : 'Excluir'}
                      </button>}
                    </div>
                  )}
                </div>
              ))}
            </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3>{editing ? 'Editar Agendamento' : 'Novo Agendamento'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Nome do cliente *</label>
                    <input value={form.client_name} onChange={e => set('client_name', e.target.value)} autoFocus />
                  </div>
                  <div className="form-group">
                    <label>Telefone / WhatsApp</label>
                    <PhoneInput
                      value={form.client_contact}
                      onChange={(masked, normalized) => set('client_contact', normalized || masked)}
                      placeholder="(11) 91234-5678"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email do cliente</label>
                    <input type="email" value={form.client_email} onChange={e => set('client_email', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Duração (minutos)</label>
                    <input type="number" min="15" step="15" value={form.duration_minutes} onChange={e => set('duration_minutes', parseInt(e.target.value)||60)} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Data e hora *</label>
                  <div className="date-input-wrap">
                    <input type="datetime-local" value={form.datetime} onChange={e => set('datetime', e.target.value)} />
                  </div>
                  {!form.datetime && <small style={{ color:'var(--gray-400)', fontSize:11 }}>Toque para selecionar a data e hora</small>}
                </div>
                {canViewAll && users.length > 0 && (
                  <div className="form-group">
                    <label>Responsável</label>
                    <select value={form.owner_user_id} onChange={e => set('owner_user_id', e.target.value)}>
                      <option value={user.id}>Eu mesmo ({user.name})</option>
                      {users.filter(u => u.id !== user.id).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                )}
                {editing && (
                  <div className="form-group">
                    <label>Status</label>
                    <select value={form.status} onChange={e => set('status', e.target.value)}>
                      <option value="scheduled">Agendado</option>
                      <option value="completed">Concluído</option>
                      <option value="cancelled">Cancelado</option>
                      <option value="no_show">Não compareceu</option>
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label>Observações</label>
                  <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Anotações sobre o agendamento..." />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className={`btn btn-primary${saving?' btn-loading':''}`} disabled={saving}>
                  {saving ? 'Salvando...' : editing ? 'Salvar' : 'Criar Agendamento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
